import type { BaseQueryApi } from '@reduxjs/toolkit/query'

import { logger } from '~/debug/logger'

import { getAccessTokenSilently, logout } from './security'

type PrepareHeadersFn = (
  headers: Headers,
  api: Pick<BaseQueryApi, 'getState' | 'extra' | 'endpoint' | 'type' | 'forced'>
) => Headers | PromiseLike<Headers>

function maybeSetOrgHeader(headers: Headers, api: Pick<BaseQueryApi, 'getState'>) {
  // Avoid overriding if the endpoint explicitly provided an org header
  if (!headers.has('X-Organization-Id')) {
    const state = api.getState() as { currentOrganization?: { cloakedOrgs?: Array<{ id: string }> } }
    const orgId = state.currentOrganization?.cloakedOrgs?.at(-1)?.id
    if (orgId) {
      headers.set('X-Organization-Id', orgId)
    }
  }
}

/**
 * Builds a `prepareHeaders` that authenticates with the Auth0 access token and, when `actAs` is set,
 * names the cloaked organization in `X-Organization-Id` so the service scopes the request to it.
 */
function authHeaders({ actAs }: { actAs: boolean }): PrepareHeadersFn {
  return async (headers, api) => {
    // Try Auth0
    try {
      const accessToken = await getAccessTokenSilently()
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`)
        if (actAs) maybeSetOrgHeader(headers, api)
        return headers
      }
    } catch {
      // Do nothing
    }

    if (actAs) maybeSetOrgHeader(headers, api)

    // Unable to resolve auth token for API Headers
    logger.info('Unable to resolve auth token for API Headers')
    logout()
    // Just for typing
    return headers
  }
}

const prepareAuthHeader = authHeaders({ actAs: true })

/**
 * Authenticates as the signed-in principal and never sends `X-Organization-Id`, so responses are
 * scoped to the principal whatever organization is cloaked. For clients whose caches hold
 * principal-wide data and narrow it to the cloaked organization on the client.
 */
export const prepareAuthHeaderWithoutOrg = authHeaders({ actAs: false })

export default prepareAuthHeader
